/* ============================================================================
   OjaOba — "Drop your list"
   Customers paste the shopping list they'd send on WhatsApp. This file:
     1) reads each line (name, quantity + unit, size, the price they wrote),
     2) finds the best real product + size in the store (Shopify search),
     3) shows a review screen with an updated estimate, then adds it all to the cart.
   Nothing is stored on a server; the list is only kept in the visitor's own browser.
   Pure helpers are exported on window.OjaDropList for testing.
   ============================================================================ */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ words */
  var NUM_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, half: 0.5 };
  var COUNT_UNITS = {
    cup: 'cup', cups: 'cup', mudu: 'mudu', mudus: 'mudu', row: 'row', rows: 'row',
    pack: 'pack', packs: 'pack', packet: 'pack', packets: 'pack', pkt: 'pack',
    pc: 'pc', pcs: 'pc', piece: 'pc', pieces: 'pc', bunch: 'bunch', bunches: 'bunch',
    basket: 'basket', baskets: 'basket', crate: 'crate', crates: 'crate', carton: 'carton', cartons: 'carton',
    bottle: 'bottle', bottles: 'bottle', tin: 'tin', tins: 'tin', sachet: 'sachet', sachets: 'sachet',
    bag: 'bag', bags: 'bag', tuber: 'tuber', tubers: 'tuber', bundle: 'bundle', bundles: 'bundle', dozen: 'dozen'
  };
  // units that mean "a multi-pack": if the product doesn't sell it by that unit, the price you wrote tells us how many
  var MULTI_UNITS = { row: 1, pack: 1, carton: 1, crate: 1, bag: 1, dozen: 1, bundle: 1 };
  var MEASURE = {
    kg: ['g', 1000], kilo: ['g', 1000], kilos: ['g', 1000], g: ['g', 1], gm: ['g', 1], gram: ['g', 1], grams: ['g', 1],
    l: ['ml', 1000], ltr: ['ml', 1000], ltrs: ['ml', 1000], litre: ['ml', 1000], litres: ['ml', 1000], liter: ['ml', 1000], liters: ['ml', 1000],
    ml: ['ml', 1], cl: ['ml', 10]
  };
  var SOFT = { fresh: 1, big: 1, small: 1, large: 1, medium: 1, new: 1, pure: 1, organic: 1, foreign: 1, local: 1, original: 1, and: 1, of: 1, the: 1, with: 1, for: 1, pls: 1, please: 1 };
  var GREETING = /^(hi|hello|hey|good\s+(morning|afternoon|evening)|please|pls|kindly|thanks?|thank\s*you|dear|regards)\b/i;
  var HEADER = /^(my|our|the|this|shopping|market|grocery|groceries|food|kitchen|weekly|monthly)?\s*(shopping\s+|market\s+|grocery\s+)?list\b.*$/i;
  var TOTAL = /^(grand\s*)?(sub\s*)?total\b/i;

  /* --------------------------------------------------------------- utilities */
  function toNumber(s) { return parseFloat(String(s).replace(/,/g, '')); }
  function naira(n) { return '₦' + Math.round(n).toLocaleString('en-US'); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  function normalizeText(t) {
    return String(t || '')
      .replace(/[‒–—―−]/g, '-')
      .replace(/[   ]/g, ' ')
      .replace(/\r/g, '');
  }

  /* ------------------------------------------------------------ list parsing */
  // Split into raw lines; commas separate items only when they aren't thousands separators (8,200).
  function splitLines(text) {
    var out = [];
    normalizeText(text).split('\n').forEach(function (ln) {
      if (isProse(ln)) { proseLines(ln).forEach(function (p) { out.push(p); }); return; }
      ln.split(/;|,(?!\d{3}(?:\D|$))/).forEach(function (part) { if (part.trim()) out.push(part.trim()); });
    });
    return out;
  }

  /* ---- long chatty messages: "Good morning ma, I want 2 bags of rice and a crate of eggs please. Deliver to Wuse. Thanks!"
     We keep the items (with their quantities) and drop greetings, small talk, delivery and payment sentences. */
  var UNITS_RE = '(?:cups?|mudus?|rows?|packs?|packets?|pkt|pcs?|pieces?|bunch(?:es)?|baskets?|crates?|cartons?|bottles?|tins?|sachets?|bags?|tubers?|bundles?|dozen|kg|kilos?|gm|grams?|g|ltrs?|litres?|liters?|ml|cl|l)';
  var NUMW_RE = '(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|half|dozen)';
  var LEAD_FILL = /^(?:and|also|plus|then|so|ok(?:ay)?|hi|hello|hey|dear|sir|ma|madam|ma'am|oga|boss|abeg|una|please|pls|kindly|just|i|we|i'?d|i'?ll|i'?m|me|my|our|want(?:ed)?|wan|need(?:ed)?|like|would|wanna|to|buy|order|get|have|collect|take|add|include|send|bring|give|put|make|you|can|could|will|shall|let|help|looking for|interested in|going to|the|some|any|few|more|extra|another|of|us)\b[\s,:'’.-]*/i;
  var TRAIL_FILL = /[\s,.!:-]*(?:please|pls|abeg|thanks|thank you|thx|asap|urgently|for me|too|as well|ok|okay|only|again|sha|o)\s*[.!]*$/i;
  var DELIVER_CUT = /\b(?:deliver(?:y|ed)?|send|bring|ship|drop)\s+(?:it|them|these|all|everything|am|the order|the items|my order)?\s*(?:to|at|before|by|in|around|on)\b.*$/i;
  var SIGNOFF_CUT = /\b(?:thank(?:s| you)|thx|regards|god bless|blessings|compliments|cheers)\b.*$/i;
  var CHATTER = /\b(?:hope|how are|how far|are you|doing|fine|good (?:morning|afternoon|evening|day|night)|call me|my number|whatsapp|address|location|landmark|estate|street|pay(?:ment)?|transfer|account|receipt|waybill|rider|dispatch|confirm|let me know|tell me|inform|how much|total|price|available|availability|do you have|can you|will you|planning|party|weekend|ceremony|birthday|wedding)\b/i;

  function hasQtyOrUnit(t) { return new RegExp('\\d|\\b' + NUMW_RE + '\\b|\\b' + UNITS_RE + '\\b', 'i').test(t); }

  function isProse(line) {
    var t = String(line || '').trim(), w = t ? t.split(/\s+/).length : 0;
    if (w >= 12) return true;
    return w >= 5 && /\b(?:i|we)\b.*\b(?:want|need|wan|would|like|am|will)\b|\b(?:please|pls|kindly|abeg|can i|could you|good (?:morning|afternoon|evening)|hello|thank)\b/i.test(t);
  }

  function cleanClause(c) {
    var guard = 0, t = c.trim();
    while (guard++ < 8 && LEAD_FILL.test(t)) t = t.replace(LEAD_FILL, '');
    guard = 0;
    while (guard++ < 5 && TRAIL_FILL.test(t) && t.replace(TRAIL_FILL, '').trim()) t = t.replace(TRAIL_FILL, '');
    // "2 bags of rice" -> "2 bags rice";  "one big bag of rice" -> "one bag rice";  "3 loaves of bread" -> "3 bread"
    t = t.replace(new RegExp('\\b(' + UNITS_RE + ')\\s+of\\s+', 'ig'), '$1 ');
    t = t.replace(new RegExp('\\b(\\d+|' + NUMW_RE + '|a|an)\\s+(?:big|small|medium|large|full|mini|nice|good)\\s+(' + UNITS_RE + ')\\b', 'ig'), '$1 $2');
    t = t.replace(/\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|a|an)\s+(?:loaf|loaves|slices?)\s+(?:of\s+)?/ig, '$1 ');
    // "for 5000" / "at N5000" / "@5000" -> "- 5000" (the price the customer wrote)
    t = t.replace(/\s(?:for|at|@)\s*(?:₦|n|ngn|#)?\s*(\d[\d,]*k?)\s*(?:naira|ngn)?\s*$/i, ' - $1');
    return t.replace(/\s{2,}/g, ' ').replace(/^[\s,.:;-]+|[\s,.;:]+$/g, '').trim();
  }

  function proseLines(line) {
    var out = [];
    String(line).replace(/([.!?])\s+/g, '$1\n').split('\n').forEach(function (sen) {
      sen = sen.trim(); if (!sen) return;
      // delivery / sign-off tails ("deliver to Wuse before 5pm", "thank you") are not items
      sen = sen.replace(DELIVER_CUT, '').replace(SIGNOFF_CUT, '').trim();
      if (!/[a-z]/i.test(sen)) return;
      if (CHATTER.test(sen) && !new RegExp('\\b' + UNITS_RE + '\\b|\\d+\\s*(?:x|pcs?|pieces?)', 'i').test(sen)) return;   // small talk / questions
      sen.split(/\s*(?:,(?!\d{3}(?:\D|$))|;|\band\b|\balso\b|\bplus\b|\bthen\b|&|\+|\bas well as\b)\s*/i).forEach(function (cl) {
        var c = cleanClause(cl);
        if (!c || !/[a-z]/i.test(c)) return;
        if (GREETING.test(c) && !/\d/.test(c)) return;
        var words = c.split(/\s+/).length;
        if (words > 7 && !hasQtyOrUnit(c)) return;                      // narrative, not an item
        if (words > 10) return;
        out.push(c);
      });
    });
    return out;
  }

  // The price a customer wrote at the end of the line, if any. Returns {value, rest} (rest = line without it).
  function takeAmount(s) {
    var m = s.match(/(?:^|\s)([-:=])\s*(?:₦|N|NGN|#)?\s*(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s*(k)?\s*(?:naira|ngn)?\s*$/i);
    if (m) {
      var v = toNumber(m[2]) * (m[3] ? 1000 : 1);
      if (v >= 50 || m[3]) return { value: v, rest: s.slice(0, m.index).trim() };
      return { qty: v, rest: s.slice(0, m.index).trim() }; // "- 3" is a quantity, not a price
    }
    m = s.match(/\s(?:₦|N|NGN|#)\s*(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s*(k)?\s*$/i);
    if (m) return { value: toNumber(m[1]) * (m[2] ? 1000 : 1), rest: s.slice(0, m.index).trim() };
    m = s.match(/\s(\d{1,3}(?:,\d{3})+|\d{3,})\s*$/);
    if (m && toNumber(m[1]) >= 200) return { value: toNumber(m[1]), rest: s.slice(0, m.index).trim() };
    return { rest: s };
  }

  function parseLine(raw) {
    var s = normalizeText(raw).trim();
    if (!s) return null;
    s = s.replace(/^\s*(?:[-*•·>]+|\d{1,2}[.)])\s+/, '').trim(); // bullets / "1. "
    if (!s || !/[a-z0-9]/i.test(s)) return null;

    var plain = s.replace(/[:\-\s]+$/, '');
    if (TOTAL.test(plain)) {
      var tm = plain.match(/(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s*(k)?\s*(?:naira)?\s*$/i);
      return { kind: 'total', value: tm ? toNumber(tm[1]) * (tm[2] ? 1000 : 1) : null, raw: raw.trim() };
    }
    if (HEADER.test(plain) && !/\d/.test(plain) && plain.split(/\s+/).length <= 5) return { kind: 'header', raw: raw.trim() };
    if (GREETING.test(plain) && !/\d/.test(plain) && plain.split(/\s+/).length <= 6) return { kind: 'skip', raw: raw.trim() };

    var item = { kind: 'item', raw: raw.trim(), name: '', qty: null, unit: null, size: null, hint: null, qtyGuess: false };

    // 1) amount written at the end
    var am = takeAmount(s);
    if (am.value != null) item.hint = am.value;
    if (am.qty != null) item.qty = am.qty;
    var r = am.rest;

    // 2) sizes like 1kg / 210g / 500ml (kept as a size, not a count)
    r = r.replace(/(\d+(?:\.\d+)?)\s*(kg|kilos?|gm|grams?|g|ltrs?|litres?|liters?|ml|cl|l)\b/gi, function (m0, n, u) {
      var spec = MEASURE[u.toLowerCase()];
      if (spec && !item.size) { item.size = { base: spec[0], v: parseFloat(n) * spec[1], label: n + u.toLowerCase() }; return ' '; }
      return m0;
    });

    // 3) "2 cups", "1row", "one mudu"
    var countRe = /\b(\d+(?:\.\d+)?|\d+\/\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten|half)\s*(?:x\s*)?(cups?|mudus?|rows?|packs?|packets?|pkt|pcs?|pieces?|bunch(?:es)?|baskets?|crates?|cartons?|bottles?|tins?|sachets?|bags?|tubers?|bundles?|dozen)\b/i;
    var cm = r.match(countRe);
    if (cm) {
      var q = cm[1].toLowerCase();
      var qv = NUM_WORDS[q] != null ? NUM_WORDS[q] : (/\//.test(q) ? (parseFloat(q.split('/')[0]) / parseFloat(q.split('/')[1])) : parseFloat(q));
      item.qty = qv; item.unit = COUNT_UNITS[cm[2].toLowerCase()];
      r = r.replace(cm[0], ' ');
    }
    // "x2" / "2x"
    var xm = r.match(/(?:\bx\s*(\d{1,3})\b|\b(\d{1,3})\s*x\b)/i);
    if (xm) { item.qty = parseFloat(xm[1] || xm[2]); r = r.replace(xm[0], ' '); }

    // 4) a unit with no number at the end of the name ("Maggi star pack", "Egusi cup")
    if (!item.unit) {
      var um = r.match(/\b(cups?|mudus?|rows?|packs?|packets?|crates?|cartons?|bunch(?:es)?|baskets?|bags?|tubers?|bundles?|dozen)\s*$/i);
      if (um && r.trim().split(/\s+/).length > 1) { item.unit = COUNT_UNITS[um[1].toLowerCase()]; if (item.qty == null) item.qty = 1; r = r.slice(0, um.index); }
    }

    // 5) a plain number: "3 plantain" / "plantain 3"
    if (item.qty == null) {
      var lead = r.match(/^\s*(\d{1,3})\s+(?=[a-z])/i);
      var trail = r.match(/\s(\d{1,2})\s*$/);
      if (lead) { item.qty = parseFloat(lead[1]); r = r.slice(lead[0].length); }
      else if (trail) { item.qty = parseFloat(trail[1]); r = r.slice(0, trail.index); }
    }

    item.name = r.replace(/[()\[\]{}]/g, ' ').replace(/[-:=|_\/]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!item.name) return { kind: 'skip', raw: raw.trim() };
    return item;
  }

  function parseList(text) {
    var items = [], userTotal = null, skipped = [];
    splitLines(text).forEach(function (ln) {
      var p = parseLine(ln);
      if (!p) return;
      if (p.kind === 'total') { if (p.value != null) userTotal = p.value; return; }
      if (p.kind === 'item') items.push(p); else skipped.push(p);
    });
    return { items: items, userTotal: userTotal, skipped: skipped };
  }

  /* ------------------------------------------------------------ text matching */
  function stem(w) {
    w = w.toLowerCase();
    if (w.length > 4) {
      if (/ies$/.test(w)) return w.slice(0, -3) + 'y';
      if (/ves$/.test(w)) return w.slice(0, -3) + 'f';
      if (/oes$/.test(w)) return w.slice(0, -2);
      if (/(ches|shes|xes|sses)$/.test(w)) return w.slice(0, -2);
    }
    if (w.length > 3 && /s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1);
    return w;
  }
  function tokens(str) {
    return String(str || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean).map(stem);
  }
  function lev(a, b) {
    if (a === b) return 0;
    var m = a.length, n = b.length, i, j, prev = [], cur;
    if (!m) return n; if (!n) return m;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur = [i];
      for (j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
    return prev[n];
  }
  function tokenSim(a, b) {
    if (a === b) return 1;
    var la = a.length, lb = b.length, mn = Math.min(la, lb);
    if (mn >= 4 && (a.indexOf(b) === 0 || b.indexOf(a) === 0)) return 0.85;
    if (mn >= 4 && (a.indexOf(b) > -1 || b.indexOf(a) > -1)) return 0.75;
    var d = lev(a, b);
    if (mn >= 3 && d === 1 && a.charAt(0) === b.charAt(0) && Math.abs(la - lb) <= 1) return 0.78;
    if (la >= 5 && lb >= 5 && d <= 1) return 0.85;
    if (la >= 8 && lb >= 8 && d <= 2) return 0.75;
    return 0;
  }
  var DERIV = { chip: 1, biscuit: 1, cracker: 1, cream: 1, soap: 1, perfume: 1, freshener: 1, flavour: 1, flavor: 1, scent: 1, lotion: 1, shampoo: 1, detergent: 1, cleaner: 1, deodorant: 1, fragrance: 1, spray: 1, candle: 1 };
  function derivPenalty(nameTokens, title) {
    var T = tokens(title);
    for (var i = 0; i < T.length; i++) if (DERIV[T[i]] && nameTokens.indexOf(T[i]) < 0) return 0.32;
    return 0;
  }
  function weight(t) { return SOFT[t] ? 0.3 : Math.min(1.5, 0.4 + t.length / 6); }
  // packaging words that say nothing about WHAT the product is (pieces, sachet, carton, nylon, jar ...)
  var PACKWORDS = { jar: 1, can: 1, tub: 1, pouch: 1, nylon: 1, sleeve: 1, tray: 1, box: 1, set: 1, mini: 1, single: 1, pieces: 1, piece: 1, sachet: 1 };
  function isUnitish(t) { return /^\d/.test(t) || !!COUNT_UNITS[t] || !!MEASURE[t] || !!PACKWORDS[t] || t === 'carton'; }

  // words in a product title that the customer did NOT write (brands, flavours ...), ignoring sizes and filler
  function extraWords(nameTokens, title) {
    return tokens(title).filter(function (t) {
      if (isUnitish(t) || SOFT[t]) return false;
      return !nameTokens.some(function (n) { return tokenSim(n, t) >= 0.75; });
    });
  }
  function extrasOf(nameTokens, title) { return extraWords(nameTokens, title).length; }

  // 0..1: how well a product title covers what the customer wrote
  function textScore(nameTokens, title) {
    var T = tokens(title);
    if (!nameTokens.length || !T.length) return 0;
    var joins = T.slice(); for (var i = 0; i < T.length - 1; i++) joins.push(T[i] + T[i + 1]);
    var nameJoins = nameTokens.slice(); for (var k = 0; k < nameTokens.length - 1; k++) nameJoins.push(nameTokens[k] + nameTokens[k + 1]);
    var total = 0, got = 0, usedTitle = {};
    nameTokens.forEach(function (n) {
      var w = weight(n), best = 0, bestT = null;
      joins.forEach(function (t) { var s = tokenSim(n, t); if (s > best) { best = s; bestT = t; } });
      nameJoins.forEach(function (nj, idx) {
        if (idx < nameTokens.length || nj.indexOf(n) < 0) return;          // only glued pairs that contain this word
        T.forEach(function (t) { var s = (nj === t ? 0.95 : (nj.length >= 6 && t.length >= 6 && lev(nj, t) <= 1 ? 0.8 : 0)); if (s > best) { best = s; bestT = t; } });
      });
      total += w; got += w * best; if (bestT) usedTitle[bestT] = 1;
    });
    var extra = T.filter(function (t) { return !usedTitle[t] && !isUnitish(t) && !SOFT[t]; }).length;
    var recall = total ? got / total : 0;
    var s = recall - Math.min(0.3, 0.05 * extra);
    if (recall > 0.99) s += 0.05;
    return Math.max(0, Math.min(1.1, s));
  }

  /* ----------------------------------------------------- variant + quantity fit */
  function variantSize(title) {
    var m = String(title || '').match(/(\d+(?:\.\d+)?)\s*(kg|kilos?|gm|grams?|g|ltrs?|litres?|liters?|ml|cl|l)\b/i);
    if (!m) return null;
    var spec = MEASURE[m[2].toLowerCase()];
    return spec ? { base: spec[0], v: parseFloat(m[1]) * spec[1] } : null;
  }
  function hasWord(text, word) { return new RegExp('\\b' + word + 's?\\b', 'i').test(String(text || '')); }

  // Best (variant, quantity) for one candidate product; returns a scored option
  function bestVariantFor(item, product) {
    var best = null;
    product.variants.forEach(function (v) {
      var unitPrice = v.price / 100, vtitle = v.title === 'Default Title' ? '' : v.title;
      var label = (product.title + ' ' + vtitle);
      var fit = 0.4, qty = null, note = '', fromAmount = false;

      // explicit size (1kg, 210g ...)
      if (item.size) {
        var vs = variantSize(vtitle) || variantSize(product.title);
        if (vs && vs.base === item.size.base) {
          if (vs.v === item.size.v) { fit += 0.4; }
          else if (item.size.v > vs.v && item.size.v % vs.v === 0 && item.size.v / vs.v <= 12) { fit += 0.15; qty = item.size.v / vs.v; note = 'size'; }
          else { fit -= 0.2; }
        }
      }
      // explicit unit (cup, mudu, row ...)
      if (item.unit) {
        if (hasWord(label, item.unit)) fit += 0.3;
        else if (!MULTI_UNITS[item.unit]) fit -= 0.05;
      }

      // quantity
      if (qty == null && item.qty != null && !(item.unit && MULTI_UNITS[item.unit] && !hasWord(label, item.unit) && item.hint && Math.abs(item.hint / unitPrice - Math.round(item.hint / unitPrice)) <= 0.12 * Math.round(item.hint / unitPrice) && Math.round(item.hint / unitPrice) >= 2 && item.qty === 1)) {
        qty = item.qty;
      }
      if (qty == null && item.unit && MULTI_UNITS[item.unit] && !hasWord(label, item.unit) && item.hint) {
        var r0 = item.hint / unitPrice, r1 = Math.round(r0);
        if (r1 >= 2 && Math.abs(r0 - r1) <= 0.12 * r1 && r1 <= 40) { qty = r1; fromAmount = true; note = 'unit-from-amount'; }
      }
      if (qty == null && item.hint && !item.size) {
        var rr = item.hint / unitPrice, rn = Math.round(rr);
        if (rn >= 2 && rn <= 30 && Math.abs(rr - rn) <= 0.06 * rn) { qty = rn; fromAmount = true; note = 'qty-from-amount'; }
      }
      if (qty == null) qty = 1;

      // closeness to the price the customer wrote
      var priceFit = 0.5;
      if (item.hint) {
        var ratio = (unitPrice * qty) / item.hint;
        priceFit = Math.max(0, 1 - Math.abs(Math.log(ratio)) / Math.log(2.2));
      }
      var score = 0.15 * Math.min(1, Math.max(0, fit)) + 0.25 * priceFit + (v.available ? 0.04 : 0) - (fromAmount ? 0.06 : 0);
      var opt = { variant: v, qty: qty, unitPrice: unitPrice, score: score, fromAmount: fromAmount, note: note, priceFit: priceFit };
      if (!best || opt.score > best.score) best = opt;
    });
    return best;
  }

  /* ------------------------------------------------------------ store lookups */
  var ROOT = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  var cache = { suggest: {}, product: {} };

  function getJSON(url) {
    return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); });
  }
  function suggest(q) {
    var key = q.toLowerCase();
    if (!cache.suggest[key]) {
      cache.suggest[key] = getJSON(ROOT + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=show')
        .then(function (j) { return (j.resources && j.resources.results && j.resources.results.products) || []; })
        .catch(function () { return []; });
    }
    return cache.suggest[key];
  }
  function productJs(handle) {
    if (!cache.product[handle]) cache.product[handle] = getJSON(ROOT + 'products/' + handle + '.js').catch(function () { return null; });
    return cache.product[handle];
  }

  function queriesFor(item) {
    var qs = [], base = item.name;
    var u = item.unit && !MULTI_UNITS[item.unit] ? item.unit : (item.unit === 'row' || item.unit === 'pack' ? item.unit : null);
    if (u && !hasWord(base, u)) qs.push(base + ' ' + u);
    qs.push(base);
    var toks = base.split(/\s+/).filter(function (t) { return t.length >= 4 && !SOFT[t.toLowerCase()]; });
    if (toks.length > 1) toks.forEach(function (t) { qs.push(t); });
    // last resort for typos: drop the final letter of long words ("sugare" -> "sugar")
    base.split(/\s+/).forEach(function (t) { if (t.length >= 5) qs.push(t.slice(0, -1)); });
    return qs.filter(function (q, i) { return qs.indexOf(q) === i; });
  }

  function imageOf(p) {
    var src = p.featured_image && (p.featured_image.url || p.featured_image) || p.image || '';
    if (typeof src === 'object') src = src.url || '';
    return src ? String(src).replace(/^(https?:)?\/\//, 'https://').replace(/(\.[a-z]+)(\?|$)/i, '_160x160$1$2') : '';
  }

  // "Dangote Sugar 250g / 500g / 1kg", "Indomie ... Carton / Half Carton", "(Nylon)": strip sizes + pack words to get the product's real name
  var SPLIT_RE = /\([^)]*\)|\b(?:half\s+|quarter\s+|full\s+)?(?:cartons?|crates?|bags?|jars?|tubs?|pouch(?:es)?|nylon|trays?|boxe?s?|sachets?)\b|\b\d+(?:\.\d+)?\s*(?:x\s*\d+(?:\.\d+)?\s*)?(?:kg|kilos?|gm|grams?|g|ml|cl|ltrs?|litres?|liters?|l|pcs?|pieces?|packs?|cups?|mudu|rows?)\b/gi;
  function splitTitle(title) {
    var parts = [], base = String(title || '').replace(SPLIT_RE, function (m) { parts.push(m.trim()); return ' '; });
    base = base.replace(/\s+[-–—]\s*$/, '').replace(/\s{2,}/g, ' ').trim();
    return { base: base || String(title || ''), label: parts.join(' ').trim() };
  }

  // every size/option of a product, in the shape the review screen uses
  function optionsOf(p) {
    return p.variants.slice(0, 60).map(function (v) {
      return { handle: p.handle, title: p.title, image: imageOf(p), variantId: v.id, variantTitle: v.title === 'Default Title' ? '' : v.title, unitPrice: v.price / 100, available: !!v.available, url: p.url };
    });
  }

  // Find the best product + size + quantity for one parsed line
  function matchItem(item) {
    var nameTokens = tokens(item.name);
    var queries = queriesFor(item), seen = {}, pool = [];

    function addResults(list) {
      list.forEach(function (p) {
        if (seen[p.handle]) return;
        seen[p.handle] = 1;
        var t = textScore(nameTokens, p.title), lo = parseFloat(p.price_min || p.price), hi = parseFloat(p.price_max || p.price);
        var near = item.hint && lo && (lo <= item.hint * 1.2 && hi >= item.hint * 0.8);
        pool.push({ handle: p.handle, title: p.title, text: t, prelim: t + (near ? 0.12 : 0) + (p.available ? 0.04 : 0), image: imageOf(p), price: lo || 0, available: !!p.available });
      });
      pool.sort(function (a, b) { return b.prelim - a.prelim; });
    }
    function run(i) {
      if (i >= queries.length) return Promise.resolve();
      return suggest(queries[i]).then(function (list) {
        addResults(list);
        if (pool.length && pool[0].text >= 0.72) return;
        return run(i + 1);
      });
    }

    return run(0).then(function () {
      var cand = pool.filter(function (c) { return c.text >= 0.3; });
      var choices = cand.slice(0, 12).map(function (c) { return { handle: c.handle, title: c.title, image: c.image, price: c.price, available: c.available }; });
      var top = cand.slice(0, 5);
      if (!top.length) {
        // half-typed word (e.g. "bre"): offer products whose name starts with it
        var pre = pool.filter(function (c) { return tokens(c.title).some(function (t) { return nameTokens.some(function (n) { return n.length >= 3 && t.indexOf(n) === 0; }); }); });
        return { item: item, status: 'notfound', options: [], choices: pre.slice(0, 12).map(function (c) { return { handle: c.handle, title: c.title, image: c.image, price: c.price, available: c.available }; }), sel: -1, qty: item.qty || 1 };
      }
      // a plain word like "Bread" fits many products equally well: don't guess, let the customer pick the kind
      var lead = top[0].text;
      var close = pool.filter(function (c) { return c.text >= 0.6 && c.text >= lead - 0.08; });
      // a product that is simply called what they typed. Packaging words ("Milk Sachet", "Eggs Crate") don't count as exact
      // unless the customer wrote that pack word themselves ("egusi 2 cups", "eggs 1 crate").
      var wantedPack = item.unit || null;
      function isPlain(c) {
        if (c.text < 0.6 || extrasOf(nameTokens, c.title) !== 0) return false;
        return tokens(c.title).every(function (t) { return !(COUNT_UNITS[t] || PACKWORDS[t]) || (wantedPack && (COUNT_UNITS[t] || t) === wantedPack); });
      }
      var plain = pool.some(isPlain);   // a product that is simply called this
      var kinds = {}; close.forEach(function (c) { extraWords(nameTokens, c.title).forEach(function (w) { kinds[w] = 1; }); });
      var names = {}; close.forEach(function (c) { names[tokens(splitTitle(c.title).base).join(' ')] = 1; });
      var wide = {}; pool.forEach(function (c) { if (c.text >= 0.65) wide[tokens(splitTitle(c.title).base).join(' ')] = 1; });
      var generic = (close.length >= 2 && Object.keys(names).length >= 2 && Object.keys(kinds).length >= 2) || Object.keys(wide).length >= 4;
      if (generic && !plain) return { item: item, status: 'pick', options: [], choices: choices, sel: -1, qty: item.qty || 1, nClose: close.length };
      return Promise.all(top.map(function (c) { return productJs(c.handle); })).then(function (prods) {
        var scored = [];
        prods.forEach(function (p, i) {
          if (!p || !p.variants || !p.variants.length) return;
          var bv = bestVariantFor(item, p);
          if (!bv) return;
          var anyIn = p.variants.some(function (v) { return v.available; });
          scored.push({ p: p, text: top[i].text, bv: bv, final: 0.6 * top[i].text + bv.score - (anyIn ? 0 : 0.25) - derivPenalty(nameTokens, top[i].title) + (isPlain(top[i]) ? 0.14 : 0) });
        });
        if (!scored.length) return { item: item, status: 'notfound', options: [], choices: choices, sel: -1, qty: item.qty || 1 };
        scored.sort(function (a, b) { return b.final - a.final; });
        var best = scored[0], options = optionsOf(best.p), sel = 0;
        options.forEach(function (o, i) { if (o.variantId === best.bv.variant.id) sel = i; });
        var chosen = options[sel];
        var second = scored[1], ambiguous = !!(second && second.p.handle !== best.p.handle && best.final - second.final < 0.08 && second.text >= 0.6);
        var status = !chosen.available ? 'soldout' : (best.text >= 0.75 && !ambiguous ? 'ok' : (best.text >= 0.45 ? 'check' : 'notfound'));
        if (status === 'notfound') return { item: item, status: 'notfound', options: [], choices: choices, sel: -1, qty: item.qty || 1 };
        return { item: item, status: status, options: options, choices: choices, sel: sel, qty: Math.max(1, Math.round(best.bv.qty * 100) / 100), note: best.bv.note, fromAmount: best.bv.fromAmount, text: best.text, ambiguous: ambiguous,
          debug: scored.map(function (c) { return c.p.title + ' | text ' + c.text.toFixed(2) + ' | final ' + c.final.toFixed(2) + ' | ' + (c.bv.variant.title || '-') + ' x' + c.bv.qty + ' @' + (c.bv.unitPrice); }) };
      });
    });
  }

  // every product matching a word, from the shop's search page (a small JSON view), 40 at a time
  function titleQuery(q) {
    return String(q || '').split(/\s+/).map(function (w) { return w.replace(/[^A-Za-z0-9'’-]/g, ''); }).filter(function (w) { return w.length >= 2; }).map(function (w) { return 'title:' + w; }).join(' ');
  }
  function fullSearch(q, page, strict) {
    var sq = strict ? (titleQuery(q) || q) : q;
    var url = ROOT + 'search?view=oja-json&type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(sq) + '&page=' + (page || 1);
    return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
      .then(function (txt) {
        var j = JSON.parse(txt);
        return { total: j.total || 0, page: j.page || 1, pages: j.pages || 1, list: (j.products || []).map(function (p) { return { handle: p.handle, title: p.title, image: p.image || '', price: (p.price || 0) / 100, available: !!p.available, variants: p.variants || [] }; }) };
      }).catch(function () { return null; });
  }

  function choiceOf(p) { return { handle: p.handle, title: p.title, image: imageOf(p), price: parseFloat(p.price_min || p.price) || 0, available: !!p.available }; }

  // fill a row from a chosen product: best size + quantity for what the customer wrote
  // Anonymous usage counts for the shop's existing Google Analytics tag. We only ever send counts, the device type and
  // catalogue product names. The customer's typed list is never sent anywhere.
  function track(name, params) {
    try {
      var p = { device: (root.matchMedia && root.matchMedia('(max-width: 768px)').matches) ? 'mobile' : 'desktop' };
      for (var k in (params || {})) p[k] = params[k];
      // Google gets a clean copy (counts only). The product lines go to our own Team Desk counter alone.
      var g = {}; for (var k2 in p) if (k2 !== '_lines') g[k2] = p[k2];
      if (typeof root.gtag === 'function') root.gtag('event', name, g);
      beacon(name, p);
    } catch (e) {}
  }

  // The same anonymous counts, also sent to OjaOba's own Team Desk stats page (admin > Apps > Team Desk > Drop list stats).
  // A random id kept in this browser lets us count each visitor once. Never the list text.
  var STATS_URL = 'https://ojaoba-team-desk.vercel.app/api/track';
  function visitorId() {
    try {
      var v = localStorage.getItem('oja_vid');
      if (!v) { v = Math.random().toString(36).slice(2, 12) + Date.now().toString(36); localStorage.setItem('oja_vid', v); }
      return v;
    } catch (e) { return ''; }
  }
  function beacon(name, p) {
    try {
      if (!/(^|\.)ojaoba\.com$/.test(root.location.hostname)) return;   // only from the real shop
      var vid = visitorId(); if (!vid) return;
      var body = JSON.stringify({ event: name, device: p.device, vid: vid, item_count: p.item_count, matched_count: p.matched_count, value: p.value, product: name === 'list_option_pick' ? p.product : undefined, lines: name === 'list_add_to_cart' ? p._lines : undefined });
      if (root.navigator && navigator.sendBeacon) navigator.sendBeacon(STATS_URL, new Blob([body], { type: 'text/plain' }));
      else if (root.fetch) root.fetch(STATS_URL, { method: 'POST', body: body, keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } });
    } catch (e) {}
  }

  function applyProduct(r, p) {
    var bv = bestVariantFor(r.item, p), opts = optionsOf(p), sel = 0;
    if (bv) opts.forEach(function (o, k) { if (o.variantId === bv.variant.id) sel = k; });
    else opts.some(function (o, k) { if (o.available) { sel = k; return true; } return false; });
    r.options = opts; r.sel = sel; r.qty = bv ? Math.max(1, Math.round(bv.qty * 100) / 100) : (r.item.qty || 1); r.note = bv ? bv.note : '';
    r.status = opts[sel].available ? 'ok' : 'soldout'; r.confirmed = true;
    return r;
  }

  // a line the customer filled in by tapping a suggestion: go straight to that product, no guessing
  function matchPinned(item, pins) {
    var raw = normalizeText(item.raw || '').toLowerCase().trim(), pin = null;
    (pins || []).forEach(function (p) { if (!pin && raw.indexOf(normalizeText(p.title).toLowerCase().trim()) === 0) pin = p; });
    if (!pin) return Promise.resolve(null);
    return Promise.all([productJs(pin.handle), suggest(item.name || pin.title)]).then(function (res) {
      var p = res[0]; if (!p || !p.variants || !p.variants.length) return null;
      return applyProduct({ item: item, status: 'ok', options: [], choices: (res[1] || []).slice(0, 12).map(choiceOf), sel: -1, qty: item.qty || 1 }, p);
    }).catch(function () { return null; });
  }

  // Parse the pasted text, then match every line (a few at a time)
  function buildPlan(text, onProgress, pins) {
    var parsed = parseList(text);
    var items = parsed.items.slice(0, 80), rows = new Array(items.length), next = 0, done = 0;
    function worker() {
      if (next >= items.length) return Promise.resolve();
      var i = next++;
      return matchPinned(items[i], pins).then(function (r) { return r || matchItem(items[i]); }).catch(function () { return { item: items[i], status: 'notfound', options: [], choices: [], sel: -1, qty: items[i].qty || 1 }; })
        .then(function (r) { rows[i] = r; done++; if (onProgress) onProgress(done, items.length); return worker(); });
    }
    var pool = []; for (var k = 0; k < Math.min(4, items.length); k++) pool.push(worker());
    return Promise.all(pool).then(function () { return { rows: rows, userTotal: parsed.userTotal, skipped: parsed.skipped, truncated: parsed.items.length > items.length }; });
  }

  /* ================================================================ the screen */
  var LS_KEY = 'oja_droplist_text';
  function lsGet() { try { return localStorage.getItem(LS_KEY) || ''; } catch (e) { return ''; } }
  function lsSet(v) { try { localStorage.setItem(LS_KEY, v); } catch (e) {} }

  function optLabel(o) { return o.title + (showVariant(o) ? ' — ' + o.variantTitle : '') + ' · ' + naira(o.unitPrice) + (o.available ? '' : ' (sold out)'); }
  function sameWords(a, b) { var n = function (x) { return String(x || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }; return n(a).indexOf(n(b)) > -1; }
  function showVariant(o) { return o.variantTitle && !sameWords(o.title, o.variantTitle); }
  function rowOpt(r) { return r.sel >= 0 ? r.options[r.sel] : null; }
  function rowQty(r) { return Math.max(1, Math.round(r.qty)); }
  function rowTotal(r) { var o = rowOpt(r); return (o && o.available) ? o.unitPrice * rowQty(r) : 0; }
  function liveRows(st) { return st.rows.filter(function (r) { return !r.removed; }); }

  function initUI() {
    var modal = document.getElementById('OjaDropList');
    if (!modal || modal.__ready) return;
    modal.__ready = true;
    document.body.appendChild(modal);

    var $ = function (sel) { return modal.querySelector(sel); };
    var ta = $('.oja-dl__ta'), stepInput = $('.oja-dl__step--input'), stepWork = $('.oja-dl__step--work'),
        stepReview = $('.oja-dl__step--review'), stepDone = $('.oja-dl__step--done'), foot = $('.oja-dl__foot'),
        progress = $('.oja-dl__progress-bar'), progressTxt = $('.oja-dl__progress-txt');
    var WA = (modal.getAttribute('data-wa') || '').replace(/\D/g, '');
    var FREE_MIN = parseFloat(modal.getAttribute('data-free-min')) || 0;
    var st = { rows: [], userTotal: null, skipped: [], truncated: false, building: false, manual: [], pins: [] };
    var lastFocus = null;

    function show(step) {
      [stepInput, stepWork, stepReview, stepDone].forEach(function (s) { s.hidden = s !== step; });
      foot.hidden = step !== stepReview;
      if (step === stepInput && typeof renderManual === 'function') renderManual();
      $('.oja-dl__body').scrollTop = 0;
    }
    function open() {
      track('list_modal_open', {});
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('oja-dl-lock');
      if (!ta.value) ta.value = lsGet();
      if (st.rows.length && !st.done) { renderReview(); show(stepReview); } else { show(stepInput); setTimeout(function () { try { ta.focus(); } catch (e) {} }, 60); }
    }
    function close() {
      closePicker();
      modal.hidden = true;
      document.documentElement.classList.remove('oja-dl-lock');
      if (lastFocus && lastFocus.focus) try { lastFocus.focus(); } catch (e) {}
    }

    /* ---- build the plan */
    function build() {
      var text = ta.value.trim();
      if (!text && !st.manual.filter(function (r) { return !r.removed; }).length) { ta.focus(); return; }
      lsSet(text);
      if (st.building) return;
      st.building = true; st.done = false;
      progress.style.width = '4%'; progressTxt.textContent = 'Reading your list…';
      show(stepWork);
      buildPlan(text, function (done, total) {
        progress.style.width = Math.max(6, Math.round(done / Math.max(1, total) * 100)) + '%';
        progressTxt.textContent = 'Finding your items… ' + done + ' of ' + total;
      }, st.pins).then(function (plan) {
        st.building = false;
        plan.rows = plan.rows.concat(st.manual.filter(function (r) { return !r.removed; }));
        st.rows = plan.rows; st.userTotal = plan.userTotal; st.skipped = plan.skipped; st.truncated = plan.truncated;
        if (!plan.rows.length) { show(stepInput); ta.focus(); toast('We couldn’t find any items in that. Put one item per line, like: Egusi 2 cups - 1000'); return; }
        track('list_submit', { item_count: plan.rows.length, matched_count: plan.rows.filter(function (r) { return r.sel >= 0; }).length });
        renderReview(); show(stepReview);
      }).catch(function () {
        st.building = false; show(stepInput);
        toast('Something went wrong while searching. Please check your connection and try again.');
      });
    }
    function toast(msg) { var t = $('.oja-dl__toast'); t.textContent = msg; t.hidden = false; clearTimeout(toast._t); toast._t = setTimeout(function () { t.hidden = true; }, 5200); }

    /* ---- review screen */
    function summaryHTML() {
      var live = liveRows(st), est = 0, hints = 0, found = 0, sold = 0, none = 0, check = 0, pick = 0;
      live.forEach(function (r) {
        est += rowTotal(r); hints += r.item.hint || 0;
        if (r.status === 'pick' && r.sel < 0) pick++;
        else if (r.status === 'notfound' || r.sel < 0) none++;
        else if (!rowOpt(r).available) sold++;
        else { found++; if (r.status === 'check' && !r.confirmed) check++; }
      });
      var wrote = st.userTotal != null ? st.userTotal : (hints || null);
      var html = '<div class="oja-dl__sum"><div class="oja-dl__sum-main"><span>Updated estimate</span><strong>' + naira(est) + '</strong></div>';
      if (wrote) {
        var diff = est - wrote;
        html += '<div class="oja-dl__sum-sub">You wrote ' + naira(wrote) + (Math.abs(diff) >= 1 ? ' · today’s prices ' + (diff > 0 ? '<b class="up">' + naira(diff) + ' more</b>' : '<b class="down">' + naira(-diff) + ' less</b>') : ' · same as today') + '</div>';
      }
      var bits = [found + ' item' + (found === 1 ? '' : 's') + ' ready'];
      if (pick) bits.push(pick + ' to choose');
      if (check) bits.push(check + ' to check');
      if (sold) bits.push(sold + ' sold out');
      if (none) bits.push(none + ' not found');
      html += '<div class="oja-dl__sum-meta">' + bits.join(' · ') + '</div>';
      if (pick) html += '<div class="oja-dl__sum-note oja-dl__sum-note--pick"><b>Tap “See options” on ' + pick + ' item' + (pick === 1 ? '' : 's') + ' to pick the kind you want.</b></div>';
      if (sold || none) html += '<div class="oja-dl__sum-note">The estimate leaves out sold-out and not-found items. Delivery isn’t included.</div>';
      else html += '<div class="oja-dl__sum-note">Delivery isn’t included.</div>';
      return html + '</div>';
    }

    function rowHTML(r, i) {
      var o = rowOpt(r), item = r.item, cls = r.status, h = '';
      if (o && !o.available) cls = 'soldout'; else if (o && r.status === 'soldout') cls = 'ok';
      if (r.status === 'check' && r.confirmed && o && o.available) cls = 'ok';
      var nChoices = (r.choices || []).length;
      var img = o ? o.image : '';
      h += '<article class="oja-dl-row oja-dl-row--' + cls + '" data-i="' + i + '">';
      h += '<div class="oja-dl-row__img">' + (img ? '<img src="' + esc(img) + '" alt="" width="64" height="64">' : '<span class="oja-dl-row__ph">?</span>') + '</div>';
      h += '<div class="oja-dl-row__main"><div class="oja-dl-row__wrote">' + (r.manual ? 'Added by you' : '“' + esc(item.raw) + '”') + '</div>';
      if (!o && r.status === 'pick') {
        h += '<div class="oja-dl-row__title">Which ' + esc(item.name) + '?</div>';
        h += '<div class="oja-dl-row__meta">We have a few kinds. Tap to see them and pick yours.</div>';
        h += '<button type="button" class="oja-dl-row__choose" data-act="browse">See options</button>';
      } else if (!o) {
        h += '<div class="oja-dl-row__title oja-dl-row__title--none">We couldn’t find “' + esc(item.name) + '”</div>';
        h += '<div class="oja-dl-row__links">' + (nChoices ? '<button type="button" class="oja-dl-row__chg" data-act="browse">See similar items</button> · ' : '') + '<a href="/search?q=' + encodeURIComponent(item.name) + '" target="_blank" rel="noopener">Search the shop</a>' + '</div>';
      } else {
        var many = r.options.length > 1;
        h += '<div class="oja-dl-row__title">' + esc(o.title) + (!many && showVariant(o) ? ' <em>' + esc(o.variantTitle) + '</em>' : '') + '</div>';
        var meta = naira(o.unitPrice) + ' each';
        if (!o.available) meta = '<b class="bad">Sold out right now</b>';
        else if (cls === 'check') meta += ' · <b class="warn">Please check this is right</b>';
        h += '<div class="oja-dl-row__meta">' + meta + '</div>';
        if (r.note === 'unit-from-amount' && o.available) h += '<div class="oja-dl-row__note">We worked out ' + rowQty(r) + ' from the ' + naira(item.hint) + ' you wrote' + (item.unit ? ' (1 ' + esc(item.unit) + ')' : '') + '.</div>';
        else if (r.note === 'size' && o.available && rowQty(r) > 1 && item.size) h += '<div class="oja-dl-row__note">Sold in ' + esc(o.variantTitle || o.title) + ', so we added ' + rowQty(r) + ' to make ' + esc(item.size.label) + '.</div>';
        else if (r.note === 'qty-from-amount' && o.available) h += '<div class="oja-dl-row__note">We worked out the quantity from the ' + naira(item.hint) + ' you wrote.</div>';
        h += variantSelectHTML(r);
        h += '<div class="oja-dl-row__btns">' + (cls === 'check' ? '<button type="button" class="oja-dl-row__yes" data-act="yes">Looks right</button>' : '') + (nChoices > 1 ? '<button type="button" class="oja-dl-row__chg" data-act="browse">Change product</button>' : '') + '</div>';
      }
      h += '</div><div class="oja-dl-row__side"><button type="button" class="oja-dl-row__rm" data-act="rm" aria-label="Remove this item">×</button>';
      if (o && o.available) {
        h += '<div class="oja-dl-row__qty"><button type="button" data-act="dec" aria-label="Less">−</button><span>' + rowQty(r) + '</span><button type="button" data-act="inc" aria-label="More">+</button></div>';
        h += '<div class="oja-dl-row__line">' + naira(rowTotal(r)) + (item.hint ? '<small>you wrote ' + naira(item.hint) + '</small>' : '') + '</div>';
      }
      return h + '</div></article>';
    }
    // size / option dropdown for the chosen product (only when it has more than one)
    function variantSelectHTML(r) {
      if (!r.options || r.options.length < 2) return '';
      var h = '<label class="oja-dl-row__change"><span>Size / option</span><select data-act="variant" aria-label="Choose a size or option">';
      r.options.forEach(function (o, k) {
        h += '<option value="' + k + '"' + (k === r.sel ? ' selected' : '') + ((!o.available && k !== r.sel) ? ' disabled' : '') + '>' + esc((o.variantTitle || 'Standard') + ' · ' + naira(o.unitPrice) + (o.available ? '' : ' (sold out)')) + '</option>';
      });
      return h + '</select></label>';
    }

    /* ---- "see options" picker: every kind of the item, tap one */
    var picker = $('.oja-dl__picker'), pkDetail = $('.oja-dl__picker-detail'), pkList = $('.oja-dl__picker-list'), pkQ = $('.oja-dl__picker-q'), pkTitle = $('.oja-dl__picker-title');
    var pk = { i: -1, t: null, seq: 0, mode: 'replace', d: null, list: [], total: 0, more: null, q: '', groups: [], open: {} };
    function cardProduct(c) {
      return { handle: c.handle, title: c.title, featured_image: c.image, url: '/products/' + c.handle, variants: c.variants };
    }
    function cardDefault(c) {
      // which size/option the dropdown starts on: the one that best fits what the customer wrote, else the first one in stock
      if (c.sel != null) return c.sel;
      var vs = c.variants || [], r = st.rows[pk.i], idx = -1;
      if (r && vs.length) { var bv = bestVariantFor(r.item, cardProduct(c)); if (bv) vs.forEach(function (v, k) { if (v.id === bv.variant.id) idx = k; }); }
      if (idx < 0) vs.some(function (v, k) { if (v.available) { idx = k; return true; } return false; });
      return c.sel = Math.max(0, idx);
    }
    // "Dangote Sugar 250g / 500g / 1kg" are separate products: show them as ONE line with the sizes underneath
    var OTHER_RE = /\b(no|free|zero|diet|added|drinks?|cereals?|muesli|crackers?|biscuits?|cookies?|cakes?|tea|soda|cola|malta|juice|yoghurt|yogurt|toothpaste|gum|candy|chocolates?|snacks?|sweeteners?|syrup|jam|spread|sauce|ketchup|lollipops?|sweets?|creams?|lotion)\b/i;
    function buildGroups(list) {
      var map = {}, order = [], nt = tokens(pk.q || '');
      list.forEach(function (c, i) {
        var sp = splitTitle(c.title), key = tokens(sp.base).join(' ') || c.handle;
        if (!map[key]) { map[key] = { key: key, name: sp.base, members: [], i: i }; order.push(map[key]); }
        map[key].members.push({ c: c, label: sp.label });
      });
      order.forEach(function (g) {
        g.members.sort(function (a, b) { return (a.c.price || 0) - (b.c.price || 0); });
        g.opts = [];
        g.members.forEach(function (m) {
          var vs = m.c.variants || [], lone = g.members.length > 1 ? m.c.title : 'Standard';
          if (vs.length > 1) vs.forEach(function (v, k) {
            var vt = v.title === 'Default Title' ? '' : v.title;
            g.opts.push({ c: m.c, vi: k, label: [m.label, vt].filter(Boolean).join(' · ') || lone, price: v.price / 100, available: !!v.available });
          });
          else g.opts.push({ c: m.c, vi: vs.length ? 0 : null, label: m.label || lone, price: vs.length ? vs[0].price / 100 : (m.c.price || 0), available: vs.length ? !!vs[0].available : !!m.c.available });
        });
        var avail = g.opts.filter(function (o) { return o.available; });
        g.available = avail.length > 0;
        g.min = (avail.length ? avail : g.opts).reduce(function (m, o) { return Math.min(m, o.price); }, Infinity);
        var withImg = g.members.filter(function (m) { return m.c.image && m.c.available; })[0] || g.members.filter(function (m) { return m.c.image; })[0] || g.members[0];
        g.image = withImg.c.image;
        g.loose = !!nt.length && OTHER_RE.test(g.name);   // e.g. "Alpen No Sugar Cereals": mentions the word but is a different kind of product
        g.sc = nt.length ? textScore(nt, g.name) : 0;
      });
      var bestSc = order.reduce(function (m, g) { return Math.max(m, g.sc); }, 0);
      if (bestSc >= 0.75) order.forEach(function (g) { if (g.sc < 0.4) g.loose = true; });   // weak spelling matches go below the line
      order.sort(function (a, b) { return ((a.loose ? 1 : 0) - (b.loose ? 1 : 0)) || ((b.available ? 1 : 0) - (a.available ? 1 : 0)) || (b.sc - a.sc) || (a.i - b.i); });   // real matches first (in stock before sold out), loose ones after, then best name match
      return order;
    }
    function pickerCards(list) {
      pk.groups = buildGroups(list);
      if (!pk.groups.length) return '<p class="oja-dl__picker-empty">Nothing found. Try another word.</p>';
      var sawReal = false, divided = false;
      return pk.groups.map(function (g, gi) {
        var multi = g.opts.length > 1, open = !!pk.open[g.key], pre = '';
        if (!g.loose) sawReal = true;
        else if (sawReal && !divided) { divided = true; pre = '<p class="oja-dl__picker-sub">Other products that mention “' + esc(pk.q) + '”</p>'; }
        var h = pre + '<div class="oja-dl-grp' + (g.available ? '' : ' oja-dl-pk--out') + (open ? ' is-open' : '') + '">';
        h += '<button type="button" class="oja-dl-pk oja-dl-grp__head" data-g-head="' + gi + '"' + (multi ? ' aria-expanded="' + open + '"' : '') + '>';
        h += '<span class="oja-dl-pk__img">' + (g.image ? '<img src="' + esc(g.image) + '" alt="" width="56" height="56">' : '') + '</span>';
        h += '<span class="oja-dl-pk__txt"><strong>' + esc(g.name) + '</strong><small>' + (!g.available ? 'Sold out right now' : (multi ? g.opts.length + ' options · from ' + naira(g.min) : naira(g.min))) + '</small></span>';
        h += '<span class="oja-dl-pk__go' + (multi ? ' oja-dl-pk__go--down' : '') + '" aria-hidden="true">' + (multi ? '⌄' : '›') + '</span></button>';
        if (multi) {
          h += '<div class="oja-dl-grp__opts"><div class="oja-dl-grp__inner">';
          g.opts.forEach(function (o, oi) {
            h += '<button type="button" class="oja-dl-opt' + (o.available ? '' : ' oja-dl-opt--out') + '" data-g="' + gi + '" data-o="' + oi + '"' + (o.available ? '' : ' disabled') + '><span>' + esc(o.label) + '</span><b>' + (o.available ? naira(o.price) : 'Sold out') + '</b></button>';
          });
          h += '</div></div>';
        }
        return h + '</div>';
      }).join('');
    }
    function renderList() {
      var cards = pickerCards(pk.list || []), n = (pk.groups || []).length;
      var h = (n > 0 && pk.total > 0) ? '<p class="oja-dl__picker-count">' + n + (pk.more ? '+' : '') + ' result' + (n === 1 ? '' : 's') + ' for “' + esc(pk.q) + '”. Tap one to see its sizes.</p>' : '';
      h += cards;
      if (pk.more) h += '<button type="button" class="oja-dl__btn oja-dl__btn--ghost oja-dl-pk-more" data-pk-more>Show more</button>';
      pkList.innerHTML = h;
    }
    function loadResults(q) {
      var seq = ++pk.seq; pk.q = q; pk.more = null; pk.open = {};
      return fullSearch(q, 1, true).then(function (res) {
        if (res && res.total === 0) return fullSearch(q, 1, false).then(function (r2) { if (r2) r2.strict = false; return r2; });
        if (res) res.strict = true;
        return res;
      }).then(function (res) {
        if (seq !== pk.seq || picker.hidden) return;
        if (!res) {
          return suggest(q).then(function (l) { if (seq !== pk.seq || picker.hidden) return; pk.list = l.map(choiceOf); pk.total = 0; renderList(); });
        }
        pk.list = res.list; pk.total = res.total; pk.more = res.pages > res.page ? { q: q, page: res.page, pages: res.pages, strict: res.strict } : null;
        renderList();
      });
    }
    function showMore() {
      var m = pk.more; if (!m) return;
      var seq = pk.seq;
      fullSearch(m.q, m.page + 1, m.strict).then(function (res) {
        if (seq !== pk.seq || !res) return;
        var have = {}; pk.list.forEach(function (c) { have[c.handle] = 1; });
        res.list.forEach(function (c) { if (!have[c.handle]) pk.list.push(c); });
        pk.more = res.pages > res.page ? { q: m.q, page: res.page, pages: res.pages, strict: m.strict } : null;
        renderList();
      });
    }
    function openPicker(i) {
      var r = st.rows[i]; if (!r) return;
      track('list_options_open', { source: r.status === 'pick' ? 'pick' : (r.sel >= 0 ? 'change' : 'not_found'), product: (r.sel >= 0 && r.options[r.sel]) ? r.options[r.sel].title : '' });
      pk.i = i; pk.mode = 'replace'; pkList.hidden = false; pkDetail.hidden = true;
      pkQ.placeholder = 'Search the shop…';
      pkTitle.textContent = 'Pick your ' + r.item.name;
      pkQ.value = r.item.name;
      pk.list = r.choices || []; pk.total = 0; pk.more = null; pk.q = r.item.name; pk.open = {}; renderList();
      pkList.scrollTop = 0;
      picker.hidden = false;
      loadResults(r.item.name);
      var b = picker.querySelector('[data-oja-dl-pkclose]'); if (b) b.focus();
    }
    function closePicker() { picker.hidden = true; pk.i = -1; pk.mode = 'replace'; pk.d = null; pkList.hidden = false; pkDetail.hidden = true; clearTimeout(pk.t); }
    function chooseProduct(handle) {
      var r = st.rows[pk.i]; if (!r) return;
      pkList.classList.add('is-busy');
      productJs(handle).then(function (p) {
        pkList.classList.remove('is-busy');
        if (!p || !p.variants || !p.variants.length) { toast('Sorry, we couldn’t open that one. Try another.'); return; }
        if (p.variants.length > 1) {
          // it comes in several sizes/options: let the customer choose (dropdown) before it goes on the list
          var bv = bestVariantFor(r.item, p), opts = optionsOf(p), sel = 0;
          if (bv) opts.forEach(function (o, k) { if (o.variantId === bv.variant.id) sel = k; });
          else opts.some(function (o, k) { if (o.available) { sel = k; return true; } return false; });
          pk.d = { p: p, opts: opts, sel: sel, qty: Math.max(1, Math.round(bv ? bv.qty : (r.item.qty || 1))), mode: 'choose' };
          pkList.hidden = true; pkDetail.hidden = false; renderDetail();
          return;
        }
        applyProduct(r, p);
        track('list_option_pick', { product: p.title, variant: (r.options[r.sel] && r.options[r.sel].variantTitle) || '', source: 'direct' });
        var idx = pk.i; closePicker(); renderReview();
        var el = modal.querySelector('.oja-dl-row[data-i="' + idx + '"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    }
    function chooseFromCard(c, vi) {
      var r = st.rows[pk.i]; if (!r) return;
      var prod = cardProduct(c), opts = optionsOf(prod);
      if (vi == null || !opts[vi]) vi = cardDefault(c);
      if (!opts[vi].available) { toast('That size is sold out right now. Please pick another.'); return; }
      var bv = bestVariantFor(r.item, prod), same = bv && bv.variant.id === opts[vi].variantId;
      r.options = opts; r.sel = vi; r.qty = same ? Math.max(1, Math.round(bv.qty * 100) / 100) : Math.max(1, Math.round(r.item.qty || 1)); r.note = same ? bv.note : '';
      r.status = 'ok'; r.confirmed = true;
      track('list_option_pick', { product: c.title, variant: opts[vi].variantTitle || '', source: 'size_pick' });
      var idx = pk.i; closePicker(); renderReview();
      var el = modal.querySelector('.oja-dl-row[data-i="' + idx + '"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    function confirmChoice() {
      var d = pk.d, r = st.rows[pk.i]; if (!d || !r) return;
      var o = d.opts[d.sel]; if (!o.available) return;
      r.options = d.opts; r.sel = d.sel; r.qty = d.qty; r.note = ''; r.status = 'ok'; r.confirmed = true;
      track('list_option_pick', { product: o.title, variant: o.variantTitle || '', source: 'size_pick' });
      var idx = pk.i; closePicker(); renderReview();
      var el = modal.querySelector('.oja-dl-row[data-i="' + idx + '"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    pkQ.addEventListener('input', function () {
      clearTimeout(pk.t);
      var q = pkQ.value.trim(), r = st.rows[pk.i], add = pk.mode === 'add';
      if (!r && !add) return;
      if (q.length < 2) { ++pk.seq; pk.total = 0; pk.more = null; pk.list = add ? [] : (r.choices || []); if (add) pkList.innerHTML = '<p class="oja-dl__picker-empty">Type at least 2 letters, then pick from the shop.</p>'; else renderList(); return; }
      pk.t = setTimeout(function () { loadResults(q); }, 280);
    });

    /* ---- "search & add an item": type, pick a product, choose size + quantity */
    function openAdder() {
      track('list_options_open', { source: 'add_item' });
      pk.i = -1; pk.mode = 'add'; pk.d = null; pkList.hidden = false; pkDetail.hidden = true;
      pkTitle.textContent = 'Add an item';
      pkQ.value = ''; pkQ.placeholder = 'Type an item, e.g. tomato';
      pk.list = []; pk.total = 0; pk.more = null; pk.open = {}; ++pk.seq;
      pkList.innerHTML = '<p class="oja-dl__picker-empty">Type at least 2 letters, then pick from the shop.</p>';
      picker.hidden = false;
      setTimeout(function () { try { pkQ.focus(); } catch (e) {} }, 80);
    }
    function openDetail(handle, variantId) {
      pkList.classList.add('is-busy');
      productJs(handle).then(function (p) {
        pkList.classList.remove('is-busy');
        if (!p || !p.variants || !p.variants.length) { toast('Sorry, we couldn’t open that one. Try another.'); return; }
        var opts = optionsOf(p), sel = 0;
        opts.some(function (o, k) { if (o.available) { sel = k; return true; } return false; });
        if (variantId != null) opts.forEach(function (o, k) { if (o.variantId === variantId && o.available) sel = k; });
        pk.d = { p: p, opts: opts, sel: sel, qty: 1, mode: 'add' };
        pkList.hidden = true; pkDetail.hidden = false; renderDetail();
        try { pkQ.blur(); } catch (e) {}
      });
    }
    function renderDetail() {
      var d = pk.d; if (!d) return;
      var o = d.opts[d.sel], many = d.opts.length > 1;
      var h = '<button type="button" class="oja-dl__back" data-ad="back">&larr; Back to results</button>';
      if (d.mode === 'choose' && st.rows[pk.i]) h += '<p class="oja-dl-ad__wrote">You wrote: “' + esc(st.rows[pk.i].item.raw) + '”</p>';
      h += '<div class="oja-dl-ad"><div class="oja-dl-ad__img">' + (o.image ? '<img src="' + esc(o.image) + '" alt="" width="84" height="84">' : '') + '</div>';
      h += '<div class="oja-dl-ad__txt"><div class="oja-dl-ad__title">' + esc(o.title) + '</div><div class="oja-dl-ad__price">' + (o.available ? naira(o.unitPrice) + ' each' : '<b class="bad">Sold out right now</b>') + '</div></div></div>';
      if (many) {
        h += '<label class="oja-dl-row__change"><span>Size / option</span><select data-ad-var aria-label="Choose a size or option">';
        d.opts.forEach(function (x, k) { h += '<option value="' + k + '"' + (k === d.sel ? ' selected' : '') + (!x.available && k !== d.sel ? ' disabled' : '') + '>' + esc((x.variantTitle || 'Standard') + ' · ' + naira(x.unitPrice) + (x.available ? '' : ' (sold out)')) + '</option>'; });
        h += '</select></label>';
      }
      h += '<div class="oja-dl-ad__row"><div class="oja-dl-row__qty"><button type="button" data-ad="dec" aria-label="Less">−</button><span>' + d.qty + '</span><button type="button" data-ad="inc" aria-label="More">+</button></div>';
      h += '<div class="oja-dl-ad__total">' + (o.available ? naira(o.unitPrice * d.qty) : '') + '</div></div>';
      h += '<button type="button" class="oja-dl__btn oja-dl__btn--primary oja-dl-ad__add" data-ad="add"' + (o.available ? '' : ' disabled') + '>' + (d.mode === 'choose' ? 'Use this one' : 'Add to my list') + '</button>';
      pkDetail.innerHTML = h;
    }
    function addManual() {
      var d = pk.d; if (!d) return;
      var o = d.opts[d.sel]; if (!o.available) return;
      var row = { item: { kind: 'item', raw: '', name: o.title, qty: d.qty, unit: null, size: null, hint: null }, status: 'ok', options: d.opts, choices: [], sel: d.sel, qty: d.qty, note: '', confirmed: true, manual: true };
      st.manual.push(row);
      track('list_option_pick', { product: o.title, variant: o.variantTitle || '', source: 'add_item' });
      var inReview = !stepReview.hidden;
      if (inReview) { st.rows.push(row); }
      closePicker();
      if (inReview) {
        renderReview();
        var el = modal.querySelector('.oja-dl-row[data-i="' + (st.rows.length - 1) + '"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
        toast('Added ' + o.title + '.');
      } else { renderManual(); toast('Added ' + o.title + '. Add more, or tap “Find my items”.'); }
    }
    function renderManual() {
      var box = $('.oja-dl__manual'); if (!box) return;
      var h = '';
      st.manual.forEach(function (r, k) {
        if (r.removed) return;
        var o = r.options[r.sel];
        h += '<div class="oja-dl__mchip"><span>' + esc(o.title) + (showVariant(o) ? ' · ' + esc(o.variantTitle) : '') + ' × ' + rowQty(r) + '</span><button type="button" data-mrm="' + k + '" aria-label="Remove ' + esc(o.title) + '">×</button></div>';
      });
      box.innerHTML = h; box.hidden = !h;
    }
    pkDetail.addEventListener('click', function (e) {
      var a = e.target.closest('[data-ad]'); if (!a || !pk.d) return;
      var act = a.getAttribute('data-ad');
      if (act === 'back') { pk.d = null; pkDetail.hidden = true; pkList.hidden = false; return; }
      if (act === 'inc') pk.d.qty = Math.min(99, pk.d.qty + 1);
      else if (act === 'dec') pk.d.qty = Math.max(1, pk.d.qty - 1);
      else if (act === 'add') { if (pk.d.mode === 'choose') confirmChoice(); else addManual(); return; }
      renderDetail();
    });
    pkDetail.addEventListener('change', function (e) {
      var s = e.target.closest('select[data-ad-var]'); if (!s || !pk.d) return;
      pk.d.sel = parseInt(s.value, 10) || 0; renderDetail();
    });

    function pickOption(o) {
      if (!o) return;
      if (!o.available) { toast('That one is sold out right now.'); return; }
      var c = o.c, hasVars = c.variants && c.variants.length;
      if (pk.mode === 'add') { openDetail(c.handle, hasVars && o.vi != null ? c.variants[o.vi].id : null); return; }
      if (hasVars && o.vi != null) chooseFromCard(c, o.vi); else chooseProduct(c.handle);
    }
    picker.addEventListener('click', function (e) {
      if (e.target.closest('[data-oja-dl-pkclose]')) { closePicker(); return; }
      if (e.target.closest('[data-pk-more]')) { showMore(); return; }
      var opt = e.target.closest('.oja-dl-opt');
      if (opt) { var og = pk.groups[parseInt(opt.getAttribute('data-g'), 10)]; if (og) pickOption(og.opts[parseInt(opt.getAttribute('data-o'), 10)]); return; }
      var head = e.target.closest('[data-g-head]'); if (!head) return;
      var g = pk.groups[parseInt(head.getAttribute('data-g-head'), 10)]; if (!g) return;
      if (!g.available) { toast('That one is sold out right now.'); return; }
      if (g.opts.length > 1) {                       // open / close the sizes underneath (animated by CSS)
        var wrap = head.closest('.oja-dl-grp'), on = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', on); head.setAttribute('aria-expanded', on ? 'true' : 'false'); pk.open[g.key] = on;
      } else pickOption(g.opts[0]);
    });

    function renderReview() {
      var html = summaryHTML();
      if (st.truncated) html += '<p class="oja-dl__note">That’s a long list! We looked at the first 80 items.</p>';
      st.rows.forEach(function (r, i) { if (!r.removed) html += rowHTML(r, i); });
      // things we can't price: say so (rows above let the customer pick something else)
      var ask = liveRows(st).filter(function (r) { return r.status !== 'pick' && (r.sel < 0 || !rowOpt(r).available); }).map(function (r) { return r.item.raw || r.item.name; });
      if (ask.length) html += '<div class="oja-dl__ask"><p><strong>' + ask.length + ' item' + (ask.length === 1 ? '' : 's') + ' won’t be added.</strong> ' + (ask.length === 1 ? 'It’s' : 'They’re') + ' sold out or we couldn’t find ' + (ask.length === 1 ? 'it' : 'them') + '. Tap “See similar items” to pick a different product, or remove ' + (ask.length === 1 ? 'it' : 'them') + '.</p></div>';
      $('.oja-dl__review').innerHTML = html;
      var addable = liveRows(st).filter(function (r) { return rowOpt(r) && rowOpt(r).available; });
      var n = addable.reduce(function (a, r) { return a + 1; }, 0), est = addable.reduce(function (a, r) { return a + rowTotal(r); }, 0);
      var btn = $('.oja-dl__add');
      btn.disabled = !n;
      btn.textContent = n ? 'Add ' + n + ' item' + (n === 1 ? '' : 's') + ' to cart · ' + naira(est) : 'Nothing to add yet';
    }
    function waLink(lines) {
      var msg = 'Hello OjaOba, I used the list tool on the website but couldn’t add these items:\n' + lines.map(function (l) { return '• ' + l; }).join('\n');
      return 'https://wa.me/' + WA + '?text=' + encodeURIComponent(msg);
    }

    /* ---- add to cart */
    function post(items) {
      var url = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
      return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); });
    }
    function cartUrl() { return ((window.theme && theme.routes && theme.routes.cart_url) || '/cart') + '.js'; }
    function getCart() { return fetch(cartUrl(), { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); }
    function qtyMap(c) { var m = {}; ((c && c.items) || []).forEach(function (i) { m[i.variant_id] = (m[i.variant_id] || 0) + i.quantity; }); return m; }
    function refreshBubble() {
      return fetch(location.pathname + '?sections=cart-bubble', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(function (s) {
        if (!s || !s['cart-bubble']) return;
        var doc = new DOMParser().parseFromString(s['cart-bubble'], 'text/html');
        ['.thb-secondary-area--item-count', '.thb-secondary-area--item-amount'].forEach(function (sel) {
          var f = doc.querySelector(sel), t = document.querySelector('#cart-drawer-toggle ' + sel); if (f && t) t.innerHTML = f.innerHTML;
        });
      }).catch(function () {});
    }
    function addAll() {
      var map = {}, order = [];
      liveRows(st).forEach(function (r) {
        var o = rowOpt(r); if (!o || !o.available) return;
        if (!map[o.variantId]) { map[o.variantId] = 0; order.push(o.variantId); }
        map[o.variantId] += rowQty(r);
      });
      var items = order.map(function (id) { return { id: id, quantity: map[id] }; });
      if (!items.length) return;
      var addLines = [];
      liveRows(st).forEach(function (r) { var o = rowOpt(r); if (o && o.available && addLines.length < 40) addLines.push({ t: String(o.title).slice(0, 80), v: String(o.variantTitle || '').slice(0, 40), q: rowQty(r) }); });
      var estValue = liveRows(st).reduce(function (a, r) { var o = rowOpt(r); return a + ((o && o.available) ? o.unitPrice * rowQty(r) : 0); }, 0);
      var btn = $('.oja-dl__add'); btn.disabled = true; btn.textContent = 'Adding to your cart…';
      // Shopify adds everything it can and only THEN reports a problem, so never retry:
      // compare the cart before and after to see exactly what went in.
      var names = {}; liveRows(st).forEach(function (r) { var o = rowOpt(r); if (o) names[o.variantId] = o.title; });
      getCart().then(function (before) {
        var was = qtyMap(before);
        return post(items).then(function () { return getCart(); }).then(function (after) {
          var now = qtyMap(after), added = 0, short = [];
          items.forEach(function (it) { var got = (now[it.id] || 0) - (was[it.id] || 0); if (got > 0) added++; if (got < it.quantity) short.push(names[it.id] || 'An item'); });
          return { added: added, failed: short.length, short: short, cart: after };
        });
      }).then(function (r) { return refreshBubble().then(function () { return r; }); })
        .then(function (r) {
          if (!r.added) { toast('Those items just sold out. Please try again or choose different ones.'); renderReview(); return; }
          track('list_add_to_cart', { item_count: r.added, value: Math.round(estValue), currency: 'NGN', _lines: addLines });
          st.done = true; showDone(r, r.cart);
        }).catch(function () { toast('Something went wrong adding to your cart. Please try again.'); renderReview(); });
    }
    function showDone(r, cart) {
      var total = cart ? cart.total_price / 100 : null, msg = '';
      if (total != null && FREE_MIN) {
        msg = total >= FREE_MIN ? '<p class="oja-dl__free oja-dl__free--yes">You’ve unlocked FREE delivery! 🎉</p>'
          : '<p class="oja-dl__free">Add <b>' + naira(FREE_MIN - total) + '</b> more for FREE delivery 🚚</p>';
      }
      $('.oja-dl__done-txt').innerHTML = '<strong>' + r.added + ' item' + (r.added === 1 ? '' : 's') + ' added to your cart.</strong>' + (r.failed ? '<span class="oja-dl__warn">' + (r.short.length > 3 ? r.short.slice(0, 3).map(esc).join(', ') + ' and ' + (r.short.length - 3) + ' more' : r.short.map(esc).join(', ')) + ' couldn’t be fully added (limited stock).</span>' : '') + msg;
      show(stepDone);
    }

    /* ---- events */
    modal.addEventListener('click', function (e) {
      var t = e.target;
      if (t === modal || t.closest('[data-oja-dl-close]')) { close(); return; }
      if (t.closest('[data-oja-dl-build]')) { build(); return; }
      if (t.closest('[data-oja-dl-clear]')) { ta.value = ''; lsSet(''); st.rows = []; st.manual = []; st.pins = []; renderManual(); showSuggHint(); ta.focus(); return; }
      if (t.closest('[data-oja-dl-additem]')) { openAdder(); return; }
      var mrm = t.closest('[data-mrm]'); if (mrm) { var mr = st.manual[parseInt(mrm.getAttribute('data-mrm'), 10)]; if (mr) { mr.removed = true; renderManual(); } return; }
      if (t.closest('[data-oja-dl-edit]')) { show(stepInput); ta.focus(); return; }
      if (t.closest('[data-oja-dl-newlist]')) { st.rows = []; st.manual = []; st.pins = []; st.done = false; ta.value = ''; lsSet(''); show(stepInput); ta.focus(); return; }
      if (t.closest('.oja-dl__add')) { addAll(); return; }
      var a = t.closest('[data-act]'); if (!a) return;
      var rowEl = a.closest('.oja-dl-row'); if (!rowEl) return;
      var r = st.rows[parseInt(rowEl.getAttribute('data-i'), 10)]; if (!r) return;
      var act = a.getAttribute('data-act');
      if (act === 'rm') r.removed = true;
      else if (act === 'inc') r.qty = Math.min(99, rowQty(r) + 1);
      else if (act === 'dec') r.qty = Math.max(1, rowQty(r) - 1);
      else if (act === 'yes') { r.confirmed = true; r.editing = false; }
      else if (act === 'browse') { openPicker(parseInt(rowEl.getAttribute('data-i'), 10)); return; }
      else return;
      renderReview();
    });
    modal.addEventListener('change', function (e) {
      var s = e.target.closest('select[data-act="variant"]'); if (!s) return;
      var r = st.rows[parseInt(s.closest('.oja-dl-row').getAttribute('data-i'), 10)]; if (!r) return;
      var k = parseInt(s.value, 10); if (k < 0) return;
      r.sel = k; r.confirmed = true; r.status = r.options[k].available ? 'ok' : 'soldout'; r.note = ''; renderReview();
    });
    /* ---- suggestions while typing: tap one to fill in the exact product name */
    var sugg = $('.oja-dl__sugg'), suggList = $('.oja-dl__sugg-list'), suggHint = $('.oja-dl__sugg-hint');
    var sg = { t: null, seq: 0, line: null, suppress: null };
    function currentLine() {
      var v = ta.value, pos = ta.selectionStart == null ? v.length : ta.selectionStart;
      var a = v.lastIndexOf('\n', Math.max(0, pos - 1)) + 1; if (pos === 0) a = 0;
      var b = v.indexOf('\n', pos); if (b < 0) b = v.length;
      return { s: a, e: b, text: v.slice(a, b) };
    }
    function typedName(line) {
      var m = /^\s*([A-Za-z][A-Za-z'’.\- ]*?)\s*(?=$|[-–—:,;]\s*\d|[-–—:,;]\s|\d)/.exec(line);
      return m ? m[1].trim() : '';
    }
    function showSuggHint(msg) { suggList.innerHTML = ''; suggHint.textContent = msg || 'Start typing an item and suggestions will appear here.'; suggHint.hidden = false; }
    function updateSugg() {
      var L = currentLine(); sg.line = L;
      if (sg.suppress !== null && L.text === sg.suppress) return;
      sg.suppress = null;
      var q = typedName(L.text);
      if (q.length < 2) { showSuggHint(); return; }
      var seq = ++sg.seq;
      suggest(q).then(function (list) {
        if (seq !== sg.seq) return;
        var cs = list.map(choiceOf).sort(function (a, b) { return (b.available ? 1 : 0) - (a.available ? 1 : 0); }).slice(0, 8);
        if (!cs.length) { showSuggHint('No match yet for “' + q + '”. Keep typing, or use “Search & add an item”.'); return; }
        suggHint.hidden = true;
        suggList.innerHTML = cs.map(function (c) {
          return '<button type="button" class="oja-dl-sg' + (c.available ? '' : ' oja-dl-sg--out') + '" data-handle="' + esc(c.handle) + '" data-title="' + esc(c.title) + '"><strong>' + esc(c.title) + '</strong><small>' + (c.available ? (c.price ? 'From ' + naira(c.price) : 'In stock') : 'Sold out') + '</small></button>';
        }).join('');
        suggList.scrollLeft = 0;
      });
    }
    function schedSugg() { clearTimeout(sg.t); sg.t = setTimeout(updateSugg, 220); }
    ta.addEventListener('input', schedSugg);
    ta.addEventListener('click', schedSugg);
    ta.addEventListener('keyup', function (e) { if (/^Arrow|Home|End/.test(e.key)) schedSugg(); });
    suggList.addEventListener('mousedown', function (e) { e.preventDefault(); });   // keep the keyboard open
    suggList.addEventListener('click', function (e) {
      var c = e.target.closest('.oja-dl-sg'); if (!c) return;
      var title = c.getAttribute('data-title'), handle = c.getAttribute('data-handle');
      var L = sg.line || currentLine();
      if (ta.value.slice(L.s, L.e) !== L.text) L = currentLine();
      var name = typedName(L.text), idx = name ? L.text.indexOf(name) : 0;
      var line = name ? L.text.slice(0, idx) + title + L.text.slice(idx + name.length) : title;
      ta.value = ta.value.slice(0, L.s) + line + ta.value.slice(L.e);
      var caret = L.s + line.length; try { ta.setSelectionRange(caret, caret); } catch (x) {}
      st.pins.push({ title: title, handle: handle });
      sg.suppress = line; lsSet(ta.value);
      showSuggHint('Got it: ' + title + '. Add the size or amount if you like, then press Enter for the next item.');
      ta.focus();
    });
    var saveT; ta.addEventListener('input', function () { clearTimeout(saveT); saveT = setTimeout(function () { lsSet(ta.value); }, 400); });
    document.addEventListener('keydown', function (e) { if (!modal.hidden && e.key === 'Escape') { if (!picker.hidden) closePicker(); else close(); } });
    document.addEventListener('click', function (e) { var b = e.target.closest && e.target.closest('[data-oja-droplist-open]'); if (b) { e.preventDefault(); track('list_banner_click', { source: b.closest('.oja-dl-card') ? 'banner' : 'bottom_bar' }); open(); } });
    API.open = open; API.close = close;
    if (/[?&]droplist=1(&|$)/.test(location.search)) {
      setTimeout(open, 250);
      try { history.replaceState(null, '', location.pathname + location.hash); } catch (e) {}
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initUI(); });
    else setTimeout(function () { initUI(); }, 0);
  }

  root.OjaDropList = root.OjaDropList || {};
  var API = root.OjaDropList;
  API._core = { parseList: parseList, parseLine: parseLine, tokens: tokens, textScore: textScore, bestVariantFor: bestVariantFor, naira: naira, esc: esc, splitLines: splitLines, matchItem: matchItem, imageOf: imageOf };
  API.parseList = parseList;
  API.textScore = textScore;
  API.buildPlan = buildPlan;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
